import { useRef, type FC, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Show } from '$/components/common/Flow/Show';
import { AdditionalFilter } from '$/components/core/Collection/CollectionFilter/components/AdditionalFilter';
import { BrightnessFilter } from '$/components/core/Collection/CollectionFilter/components/BrightnessFilter';
import { FilterWrapper } from '$/components/core/Collection/CollectionFilter/components/FilterWrapper';
import { MultiFilter } from '$/components/core/Collection/CollectionFilter/components/MultiFilter';
import { ProMenuItem } from '$/components/core/Collection/CollectionFilter/components/ProMenuItem';
import { SingleFilter } from '$/components/core/Collection/CollectionFilter/components/SingleFilter';
import { FilterDropdown } from '$/components/core/Collection/CollectionFilter/FilterDropdown';
import { FilterSkeleton } from '$/components/core/Collection/CollectionFilter/FilterSkeleton';
import { useFilterOptions } from '$/hooks/data/useFilterOptions';
import { useCollectionStore } from '$/stores/useCollectionStore';
import { repeat } from '$/utils/arrayUtils';

interface Props {
  sectionRef: RefObject<HTMLDivElement | null>;
  hideAmounts?: boolean;
}

export const DesktopFilterMenu: FC<Props> = ({
  sectionRef,
  hideAmounts = false,
}) => {
  const { t } = useTranslation();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRefs = [wrapperRef, sectionRef];

  const activeType = useCollectionStore.useActiveType();
  const { data: possibleFilter } = useFilterOptions();

  return (
    <FilterWrapper ref={wrapperRef}>
      <Show
        fallback={() =>
          repeat(3).map((index) => (
            <FilterSkeleton key={index} title='Hersteller' />
          ))
        }
        when={possibleFilter}
      >
        {(filter) => {
          return (
            <>
              {filter.general.map((filterGroup) => {
                return (
                  <FilterDropdown
                    key={filterGroup.searchField}
                    dataTestId={filterGroup.searchField}
                    title={filterGroup.displayName}
                    scrollRefs={scrollRefs}
                  >
                    {(onMenuClose) => {
                      if (filterGroup.isMultiSelect) {
                        return (
                          <MultiFilter
                            onMenuClose={onMenuClose}
                            hideAmounts={hideAmounts}
                            filter={filterGroup}
                          />
                        );
                      }

                      if (!filterGroup.isMultiSelect) {
                        return (
                          <>
                            <SingleFilter
                              onMenuClose={onMenuClose}
                              hideAmounts={hideAmounts}
                              filter={filterGroup}
                            />
                            <ProMenuItem name={filterGroup.displayName} />
                          </>
                        );
                      }
                    }}
                  </FilterDropdown>
                );
              })}

              {filter.additional.length > 0 && (
                <FilterDropdown
                  title={t('dashboard.collection.filter.additional')}
                  scrollRefs={scrollRefs}
                >
                  {(onMenuClose) => (
                    <AdditionalFilter
                      onMenuClose={onMenuClose}
                      additionalFilter={filter.additional}
                    />
                  )}
                </FilterDropdown>
              )}

              {activeType === 'color_tint' && (
                <FilterDropdown
                  title={t('dashboard.collection.filter.brightness')}
                  scrollRefs={scrollRefs}
                >
                  {(onMenuClose) => (
                    <BrightnessFilter onMenuClose={onMenuClose} />
                  )}
                </FilterDropdown>
              )}
            </>
          );
        }}
      </Show>
    </FilterWrapper>
  );
};
